import { chartPointToPixel } from "../coordinateUtils";
import { createDrawingObject, replaceLastPoint } from "../shared";
import { toOverlayPrimitive } from "../overlayPrimitiveAdapter";
import { hitTestOverlayPrimitive, renderOverlayPrimitiveToCanvas } from "@vninvestcharts/engine-core";
export function calculateAnchoredVwapGeometry(drawing, scales, plotData) {
    const anchorPoint = drawing.points[0];
    if (!anchorPoint || !plotData || plotData.length === 0)
        return null;
    // Find the anchor index in plotData
    const anchorTime = anchorPoint.timeMsHint ?? anchorPoint.x;
    let anchorIndex = plotData.findIndex(d => {
        const time = d.time ?? (d.date instanceof Date ? d.date.getTime() : d.date);
        return time >= anchorTime;
    });
    if (anchorIndex === -1)
        anchorIndex = 0;
    // Calculate VWAP from anchor to current
    let cumulativeVolume = 0;
    let cumulativeVolumePrice = 0;
    const vwapLine = [];
    const upperBand = [];
    const lowerBand = [];
    const bandMultiplier = drawing.style.bandMultiplier ?? 1;
    for (let i = anchorIndex; i < plotData.length; i++) {
        const bar = plotData[i];
        const typicalPrice = (bar.high + bar.low + bar.close) / 3;
        cumulativeVolume += bar.volume;
        cumulativeVolumePrice += typicalPrice * bar.volume;
        if (cumulativeVolume > 0) {
            const vwap = cumulativeVolumePrice / cumulativeVolume;
            const barTime = bar.time ?? (bar.date instanceof Date ? bar.date.getTime() : bar.date);
            const point = chartPointToPixel({ x: barTime, y: vwap }, scales);
            vwapLine.push(point);
            // Calculate standard deviation for bands
            // Simplified: use a running standard deviation approximation
            const stdDev = Math.sqrt(Math.max(0, (bar.high - bar.low) * (bar.high - bar.low) / 12));
            const bandOffset = stdDev * bandMultiplier;
            upperBand.push({ x: point.x, y: point.y - bandOffset * (scales.yScale(0) - scales.yScale(1)) });
            lowerBand.push({ x: point.x, y: point.y + bandOffset * (scales.yScale(0) - scales.yScale(1)) });
        }
    }
    const anchorPixel = chartPointToPixel(anchorPoint, scales);
    const lastVwap = vwapLine[vwapLine.length - 1]?.y ?? anchorPixel.y;
    const vwapValue = scales.yScaleInvert(lastVwap) ?? 0;
    return {
        anchor: anchorPixel,
        vwapLine,
        upperBand,
        lowerBand,
        vwapValue,
    };
}
function renderAnchoredVwap(ctx, drawing, scales, options) {
    const primitive = toOverlayPrimitive(drawing, scales, {
        chartWidth: options.chartWidth,
        chartHeight: options.chartHeight,
        plotData: options.plotData,
    });
    if (primitive) {
        // Import and use the engine's render function
        renderOverlayPrimitiveToCanvas(ctx, primitive, options);
    }
}
function hitTestAnchoredVwap(drawing, point, scales, tolerance, plotData) {
    const primitive = toOverlayPrimitive(drawing, scales, { plotData });
    if (!primitive)
        return false;
    return hitTestOverlayPrimitive(primitive, point.pixelX, point.pixelY, { tolerance, chartWidth: 800, chartHeight: 600 });
}
const AnchoredVWAP = {
    name: "anchoredVwap",
    createDraft: (startPoint) => createDrawingObject("anchoredVwap", [startPoint], {
        style: { stroke: "#FF6D00", strokeWidth: 2, fill: "rgba(255,109,0,0.1)", fillOpacity: 0.1 },
    }),
    updateDraft: (draft, nextPoint) => replaceLastPoint(draft, nextPoint),
    render: () => undefined,
    hitTest: hitTestAnchoredVwap,
};
export default AnchoredVWAP;
